import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './FAQ.css';

type FaqItem = { q: string; a: ReactNode };
type FaqCategory = { id: string; title: string; items: FaqItem[] };

const categories: FaqCategory[] = [
  {
    id: 'what-is',
    title: 'What is mosaime?',
    items: [
      {
        q: 'What is mosaime?',
        a: (
          <p>
            mosaime is an AI-powered wellness coach delivered entirely over text message. There's no
            app to download and no dashboard to navigate. You log meals, workouts, sleep, and mood by
            texting naturally — the way you'd describe your day to a coach who actually knows you —
            and get real, personalized coaching back.
          </p>
        ),
      },
      {
        q: 'Is mosaime a human coach or an AI?',
        a: (
          <>
            <p>
              mosaime is AI-powered, not a human. But it's built specifically for wellness coaching
              — not general-purpose chat. Every response is grounded in a single, published wellness
              philosophy and your personal goals, habits, and history.
            </p>
            <p>
              Think of it as a knowledgeable, supportive coach who's available by text at any time,
              gets more useful the longer you use it, and never makes you feel judged for an off day.
            </p>
          </>
        ),
      },
      {
        q: 'What makes mosaime different from other wellness apps?',
        a: (
          <>
            <p>A few things stand out:</p>
            <p>
              <strong>No app.</strong> mosaime lives in your text messages — the place you already
              check constantly. There's nothing to download, no interface to learn, and no updates to
              install.
            </p>
            <p>
              <strong>Coaching, not just tracking.</strong> mosaime works toward specific goals and
              habits with you, grounded in a real wellness philosophy. It doesn't just log your data
              and leave you to interpret it yourself.
            </p>
            <p>
              <strong>One picture, not a scoreboard.</strong> Instead of 20 separate charts, you get
              a single Wellness Mosaic — one view of your diet, exercise, and mental health together.
              A few dimmer tiles next to brighter ones isn't a problem to fix. That's what balance
              looks like.
            </p>
            <p>
              <strong>A published philosophy.</strong> mosaime's coaching is anchored to a specific,
              publicly readable wellness framework. You can read exactly what shapes every response
              before you sign up at <Link to="/philosophy">mosaime.com/philosophy</Link>.
            </p>
          </>
        ),
      },
      {
        q: 'What does mosaime actually coach me on?',
        a: (
          <>
            <p>mosaime covers three areas — tracked and coached together, not in isolation:</p>
            <p>
              <strong>Diet & Nutrition</strong> — meal logging by text or photo, calorie and macro
              tracking, food quality signals, ingredient label scanning, recipes, meal plans, and
              grocery lists.
            </p>
            <p>
              <strong>Exercise & Movement</strong> — workout logging, custom training plans, daily
              workout links, Fitbit integration, and support for natural movement alongside
              structured exercise.
            </p>
            <p>
              <strong>Sleep & Mental Health</strong> — sleep tracking, mood and stress check-ins,
              breathwork nudges, journaling prompts, and daily cognitive puzzles.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: 'getting-started',
    title: 'Getting started',
    items: [
      {
        q: 'Do I need a smartphone?',
        a: (
          <p>
            Any phone that can send and receive SMS texts works with mosaime. That said, mosaime
            regularly sends links to richer views — your weekly Wellness Mosaic, recipes, workout
            plans, daily coaching briefs — that are easier to open on a smartphone or computer. A
            smartphone isn't required, but you'll get more out of mosaime with one.
          </p>
        ),
      },
      {
        q: 'How does setup work?',
        a: (
          <>
            <p>Setup takes about 5 minutes:</p>
            <ol>
              <li>Sign up with your name, email, and US phone number.</li>
              <li>Verify your phone with a 6-digit code sent by text.</li>
              <li>
                Answer a short intake survey — about 15 questions covering your schedule, diet,
                exercise habits, and wellness goals.
              </li>
              <li>Optionally build a workout plan.</li>
              <li>Within minutes, mosaime sends your first text. From there, you just reply naturally.</li>
            </ol>
          </>
        ),
      },
      {
        q: 'Is mosaime available outside the US?',
        a: (
          <p>
            mosaime currently requires a US phone number for SMS. International availability is
            something we're working toward — if you're outside the US and interested, reach out at{' '}
            <a href="mailto:support@mosaime.com">support@mosaime.com</a> and we'll let you know when
            that changes.
          </p>
        ),
      },
    ],
  },
  {
    id: 'texting',
    title: 'How texting works',
    items: [
      {
        q: 'What number do I text?',
        a: (
          <p>
            When you sign up and verify your phone, mosaime's number is automatically saved to your
            contacts. All your coaching happens in that one conversation thread.
          </p>
        ),
      },
      {
        q: 'How often will mosaime text me?',
        a: (
          <>
            <p>
              On a typical day, mosaime sends 3–5 messages: a morning greeting with your coaching
              brief, a mid-morning check-in, a lunch nudge, and an evening wind-down prompt. On
              Sundays it also sends your weekly summary and Wellness Mosaic.
            </p>
            <p>
              You control how much mosaime reaches out. If you want more or fewer proactive messages,
              just tell it — it'll adjust.
            </p>
          </>
        ),
      },
      {
        q: 'What if I want to stop getting texts?',
        a: (
          <p>
            Reply <strong>STOP</strong> at any time to pause all messages. Reply <strong>HELP</strong>{' '}
            for support info. Standard message and data rates from your carrier may apply.
          </p>
        ),
      },
      {
        q: 'Can I text mosaime about anything wellness-related?',
        a: (
          <>
            <p>
              Yes — mosaime is conversational and handles a wide range of wellness topics. Log meals,
              ask for recipe ideas, request a workout, share how you slept, ask about nutrition, check
              in on a habit. It's built for this.
            </p>
            <p>
              There are a few things mosaime won't do: diagnose conditions, interpret lab results,
              recommend medications or supplements, or act as a therapist. If something you share
              touches those areas, mosaime will say so and point you toward appropriate professional
              support.
            </p>
          </>
        ),
      },
      {
        q: 'Can I log things that happened earlier in the day?',
        a: (
          <p>
            Yes. mosaime can backfill entries — just describe what happened and when. "I had coffee
            this morning around 7" will log to the right time.
          </p>
        ),
      },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy & data',
    items: [
      {
        q: 'What data does mosaime collect?',
        a: (
          <p>
            mosaime collects what you share with it — your messages, meal descriptions, workout logs,
            mood check-ins — along with basic account information (name, email, phone, timezone) and,
            if connected, Fitbit activity and sleep data. This is used to personalize your coaching
            and improve mosaime's ability to help you. We don't sell your personal information. Full
            details are in our <Link to="/privacy">Privacy Policy</Link>.
          </p>
        ),
      },
      {
        q: 'Is my health data secure?',
        a: (
          <p>
            Yes. Your data is encrypted in transit and at rest, and access is limited to what's
            needed to deliver your coaching. See our <Link to="/privacy">Privacy Policy</Link> for the
            full picture.
          </p>
        ),
      },
      {
        q: 'Can I delete my data?',
        a: (
          <p>
            Yes. To request deletion of your account and all associated data, contact us at{' '}
            <a href="mailto:support@mosaime.com">support@mosaime.com</a>. We'll process your request
            promptly.
          </p>
        ),
      },
    ],
  },
  {
    id: 'coaching',
    title: 'The wellness coaching',
    items: [
      {
        q: "What's the wellness philosophy behind mosaime's coaching?",
        a: (
          <>
            <p>
              mosaime's coaching is grounded in a holistic, evidence-informed framework: sustainable
              wellbeing comes from integrating diet, movement, and mental health — not optimizing them
              separately.
            </p>
            <p>
              The core idea is <strong>progress over perfection</strong>: off days, missed workouts,
              and occasional indulgences are a normal part of a healthy life. mosaime never
              guilt-trips you for an imperfect choice — it cares about your overall pattern over weeks
              and months, not any single decision.
            </p>
            <p>
              You can read the full philosophy at <Link to="/philosophy">mosaime.com/philosophy</Link>.
              It's published so the coaching is never a black box.
            </p>
          </>
        ),
      },
      {
        q: 'Can mosaime replace my doctor or therapist?',
        a: (
          <p>
            No. mosaime is a wellness coach, not a medical or mental health provider. It doesn't
            diagnose conditions, interpret lab results, recommend medications, or provide therapy. If
            something you share touches those areas, mosaime will acknowledge it and point you toward
            appropriate professional support. For anything involving an existing health condition,
            please work with your doctor.
          </p>
        ),
      },
      {
        q: 'Does mosaime work for any fitness level or dietary approach?',
        a: (
          <p>
            Yes. mosaime's philosophy is flexible and non-prescriptive — it works with your goals,
            your preferences, and your life, not a one-size-fits-all template. Whether you're just
            starting out, training for a specific event, managing dietary restrictions, or somewhere
            in between, mosaime adjusts to fit where you are.
          </p>
        ),
      },
    ],
  },
  {
    id: 'fitbit',
    title: 'Fitbit & integrations',
    items: [
      {
        q: 'Does mosaime require a Fitbit?',
        a: (
          <p>
            No. mosaime works fully without a Fitbit — you can log all activity and sleep by text.
            Connecting Fitbit adds passive, automatic tracking on top of what you report: steps,
            active minutes, heart rate, and sleep data flow in automatically so you don't have to.
          </p>
        ),
      },
      {
        q: 'How do I connect my Fitbit?',
        a: (
          <p>
            Go to your account settings at <Link to="/settings">mosaime.com/settings</Link> and follow
            the Fitbit connection steps. You'll authorize mosaime to read your Fitbit data — mosaime
            only reads, never writes to your Fitbit account.
          </p>
        ),
      },
      {
        q: 'Will you add support for Apple Health, Garmin, or other wearables?',
        a: (
          <p>
            We're working on expanding integrations. If there's a specific device or platform you'd
            like supported, let us know at{' '}
            <a href="mailto:support@mosaime.com">support@mosaime.com</a> — user interest shapes the
            roadmap.
          </p>
        ),
      },
    ],
  },
];

function FaqAccordionItem({ item }: { item: FaqItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-item-open' : ''}`}>
      <button
        type="button"
        className="faq-question"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{item.q}</span>
        <span className="faq-chevron" aria-hidden="true">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="faq-answer">{item.a}</div>}
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="landing-page">
      <PublicHeader />

      {/* ---- HERO ---- */}
      <section className="faq-hero">
        <div className="landing-container">
          <h1>Frequently asked questions.</h1>
          <p className="faq-hero-sub">
            Can't find what you're looking for?{' '}
            <a href="mailto:support@mosaime.com">Contact us</a> — we're happy to help.
          </p>
        </div>
      </section>

      {/* ---- FAQ CONTENT ---- */}
      <section className="section faq-content">
        <div className="landing-container faq-inner">
          {categories.map((category) => (
            <div key={category.id} className="faq-category">
              <h2 className="faq-category-title">{category.title}</h2>
              <div className="faq-list">
                {category.items.map((item) => (
                  <FaqAccordionItem key={item.q} item={item} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- FOOTER CTA ---- */}
      <section className="section cta-section">
        <div className="landing-container cta-inner">
          <h2>Still have questions?</h2>
          <p className="cta-sub">
            We're happy to help. <a href="mailto:support@mosaime.com" className="cta-sub-link">Reach out anytime.</a>
          </p>
          <Link to="/signup" className="btn hero-cta">Start Free — 7-Day Trial</Link>
        </div>
      </section>

      {/* ---- FOOTER ---- */}
      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/features">Features</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/philosophy">Philosophy</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} mosaime. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
